import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { z } from "zod";
import { api, formatApiError, track } from "@/lib/api";
import { Cta } from "@/components/Cta";
import { EASE } from "@/components/motion";
import { lenisRef } from "@/lib/lenis";

export function Field({ label, name, error, optional, children, hint }) {
  return (
    <div>
      <label htmlFor={name} className="field-label">{label}{optional && <span className="ml-2 font-normal text-si-muted">(optional)</span>}</label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-si-muted">{hint}</p>}
      {error && <p id={`${name}-error`} role="alert" className="mt-1.5 text-sm text-[#ff8f8f]" data-testid={`error-${name}`}>{error}</p>}
    </div>
  );
}

export function useForm({ initial, schema, endpoint, kind }) {
  const { pathname } = useLocation();
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [started, setStarted] = useState(false);
  const bind = (name, type = "text") => ({
    id: name, name, "data-testid": `input-${name}`, "aria-invalid": !!errors[name], "aria-describedby": errors[name] ? `${name}-error` : undefined,
    ...(type === "checkbox"
      ? { checked: !!values[name], onChange: (e) => setValues((v) => ({ ...v, [name]: e.target.checked })) }
      : { value: values[name] ?? "", onChange: (e) => {
          if (!started) { setStarted(true); track(`${kind}_started`, { page: pathname }); }
          setValues((v) => ({ ...v, [name]: e.target.value }));
        } }),
  });
  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fe = {};
      parsed.error.issues.forEach((i) => { fe[i.path[0]] = fe[i.path[0]] || i.message; });
      setErrors(fe);
      document.getElementById(Object.keys(fe)[0])?.focus();
      return;
    }
    setErrors({});
    setServerError("");
    setState("loading");
    track(`${kind}_submitted`, { page: pathname });
    try {
      const { data } = await api.post(endpoint, { ...values, source_page: pathname });
      if (!data?.ok) throw new Error("Submission was not confirmed. Please try again.");
      setState("success");
      track(`${kind}_succeeded`, { page: pathname });
    } catch (err) {
      setServerError(formatApiError(err));
      setState("error");
      track(`${kind}_failed`, { page: pathname });
    }
  };
  return { values, errors, state, serverError, bind, submit };
}

export function SuccessPanel({ title, text, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -120 });
    else el.scrollIntoView({ block: "center" });
  }, []);
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }}
      role="status" className="rounded-3xl border border-emerald-400/30 bg-emerald-400/5 p-10 text-center" data-testid={testId}>
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-300" />
      <h3 className="mt-6 text-3xl font-bold tracking-tight">{title}</h3>
      <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-si-muted">{text}</p>
    </motion.div>
  );
}

export function SubmitRow({ state, serverError, label, testId }) {
  return (
    <div className="space-y-4">
      {state === "error" && (
        <div role="alert" className="flex gap-3 rounded-xl border border-red-400/30 bg-red-400/5 p-4 text-sm text-[#ffb0b0]" data-testid={`${testId}-error`}>
          <AlertCircle className="h-5 w-5 shrink-0" />{serverError}
        </div>
      )}
      <Cta type="submit" disabled={state === "loading"} size="lg" testId={testId} className="w-full sm:w-auto">
        {state === "loading" ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Sending…</span> : label}
      </Cta>
    </div>
  );
}

export function Honeypot({ bind }) {
  return (
    <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
      <label htmlFor="website">Website</label>
      <input tabIndex={-1} autoComplete="off" {...bind("website")} />
    </div>
  );
}

export function Consent({ bind, error, children }) {
  return (
    <div>
      <label className="flex cursor-pointer gap-3 text-sm leading-relaxed text-si-muted">
        <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#17B6F4]" {...bind("consent", "checkbox")} />
        <span>{children}</span>
      </label>
      {error && <p id="consent-error" role="alert" className="mt-1.5 text-sm text-[#ff8f8f]" data-testid="error-consent">{error}</p>}
    </div>
  );
}

export const consentRule = z.literal(true, { errorMap: () => ({ message: "Please confirm so we can process your submission." }) });
